/**
 * Cortex Command Centre v2 — Agentic Operator persona scenarios.
 *
 * 8 top-scoring scenarios from Pupil-inspired evaluation (avg 0.912).
 * Each demonstrates a real Cortex capability backed by production code.
 *
 * Persona: Agentic Operator — orchestrates AI agent teams, builds systems
 * Core thesis: Intelligence that compounds. Session amnesia eliminated.
 * Philosophy: Confidence is earned, not asserted. Honesty compounds.
 */

export interface OutputLine {
  text: string;
  type: 'header' | 'info' | 'success' | 'warning' | 'error' | 'dimmed' | 'accent' | 'data';
}

export interface Highlight {
  label: string;
  value: string;
  color: 'cyan' | 'green' | 'yellow' | 'red';
}

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  command?: string;
  outputLines: OutputLine[];
  highlight?: Highlight;
}

export const SCENARIOS: Scenario[] = [
  // ── 1. Cross-Session Memory (score: 0.950) ────────────
  // Core value prop: session amnesia eliminated
  {
    id: 'memory',
    title: 'Cross-Session Memory',
    subtitle: 'What you learned 3 weeks ago, surfaced when you need it',
    category: 'Session Intelligence',
    command: 'cortex intelligence "wind data returning NaN after refactor"',
    outputLines: [
      { text: '⚡ Memory match found', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  "GRIB interp() needs 0-360 lon convention"', type: 'accent' },
      { text: '  Stored: Session #412, Feb 21 (Vortex)', type: 'dimmed' },
      { text: '  Confidence: 0.89 | Accessed 4 times', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  Context: ds.interp() does NOT auto-convert', type: 'info' },
      { text: '  negative lon on 0-360 grids. Use lon_use', type: 'info' },
      { text: '  from _get_grid_indices() for both sel() and interp().', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  → This issue has been solved. Apply known fix.', type: 'success' },
    ],
    highlight: { label: 'Session Amnesia', value: 'Eliminated — 18 months of knowledge retained', color: 'green' },
  },

  // ── 2. Cross-Project Pattern Search (score: 0.932) ────
  // Portfolio intelligence — what worked elsewhere
  {
    id: 'patterns',
    title: 'Pattern Search',
    subtitle: 'What worked in Project A might solve your problem in Project B',
    category: 'Intelligence',
    command: 'cortex intelligence "implement rate limiting"',
    outputLines: [
      { text: '═══ Pattern Matches (3 found) ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  ▸ Alpha Arena: Token bucket, 500 req/min', type: 'info' },
      { text: '    Production 3 months, 0 incidents', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  ▸ Vortex API: SlowAPI middleware, 100/min per IP', type: 'info' },
      { text: '    3 months uptime, well-tested', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  ▸ Anti-pattern: Never rate-limit health endpoints', type: 'warning' },
      { text: '    Source: PR #312 postmortem', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  → Reuse SlowAPI pattern from Vortex (proven)', type: 'accent' },
    ],
    highlight: { label: 'Cross-Project Learning', value: '6 projects → patterns compound', color: 'cyan' },
  },

  // ── 3. Root Cause Shortcut (score: 0.932) ─────────────
  // Known error → instant fix from memory
  {
    id: 'debug',
    title: 'Root Cause Shortcut',
    subtitle: 'This error has been solved before — here\'s the fix',
    category: 'Intelligence',
    command: 'cortex debug "ImportError: circular import"',
    outputLines: [
      { text: '⚡ Known issue — exact match', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  Root cause: Circular import between', type: 'info' },
      { text: '  model_competition.py ↔ ensemble_analyzer.py', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  Fix: Import inside functions, not at module level', type: 'accent' },
      { text: '  Recorded: Session #287, Jan 15 (Alpha Arena)', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  Prevention: Added to gotchas database', type: 'success' },
      { text: '  Similar issues caught proactively: 3 times since', type: 'dimmed' },
    ],
    highlight: { label: 'Time Saved', value: 'Known fix applied in seconds, not hours', color: 'green' },
  },

  // ── 4. Edit-Without-Read Guard (score: 0.922) ─────────
  // Proven anti-pattern prevention from 59,917 events
  {
    id: 'guardian',
    title: 'Edit Guard',
    subtitle: 'Prevents blind file edits — the #1 cause of wasted cycles',
    category: 'Anti-Pattern Prevention',
    command: '[GUARDIAN — auto-triggered on Edit tool call]',
    outputLines: [
      { text: '⚠ GUARDIAN: Blind edit detected', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  You\'re editing auth.py without reading it first', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  Evidence (59,917 events audited):', type: 'dimmed' },
      { text: '  ▸ Blind edit failure rate: 75.7%', type: 'error' },
      { text: '  ▸ Read-first success rate: 91.2%', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  Action: Read the file first, then edit', type: 'accent' },
      { text: '  [Override available — always your call]', type: 'dimmed' },
    ],
    highlight: { label: 'Prevention Rate', value: '75.7% of blind edits fail — this one won\'t', color: 'yellow' },
  },

  // ── 5. Morning Briefing (score: 0.907) ────────────────
  // 10-second portfolio overview to start the day
  {
    id: 'briefing',
    title: 'Morning Briefing',
    subtitle: 'Portfolio health, anomalies, and next actions in 10 seconds',
    category: 'Session Intelligence',
    command: 'cortex briefing',
    outputLines: [
      { text: '═══ Daily Intelligence Briefing ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  ▸ 6 projects — 5 green, 1 needs attention', type: 'info' },
      { text: '  ▸ Backend: 2,126 tests passing (0 regressions)', type: 'success' },
      { text: '  ▸ Anomaly: Winfield DOWN (176 consecutive failures)', type: 'error' },
      { text: '  ▸ EMOS: HRRR ready (38,992 pairs), GFS needs 1,919', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  Goal velocity: +23% vs last week', type: 'success' },
      { text: '  Stale: Goal 3 (no commits in 5 days)', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  → Next: Resume XGBoost retrain for ML nowcaster', type: 'accent' },
    ],
    highlight: { label: 'Portfolio', value: '5,000+ tests across 6 projects', color: 'cyan' },
  },

  // ── 6. Validated-But-Not-Shipped (score: 0.890) ───────
  // The most dangerous anti-pattern in agentic development
  {
    id: 'unshipped',
    title: 'Ship Gate',
    subtitle: 'The most dangerous anti-pattern — validated but never deployed',
    category: 'Anti-Pattern Prevention',
    command: 'cortex audit --unshipped',
    outputLines: [
      { text: '⚠ ANTI-PATTERN: Validated but not deployed', type: 'error' },
      { text: '', type: 'dimmed' },
      { text: '  XGBoost V2 Nowcaster:', type: 'info' },
      { text: '  ▸ Validated: +22% improvement over single models', type: 'success' },
      { text: '  ▸ Tests: 104 passing, all assertions strict', type: 'success' },
      { text: '  ▸ Status: NOT wired to production endpoint', type: 'error' },
      { text: '', type: 'dimmed' },
      { text: '  Risk: Context decays. Re-validation needed later.', type: 'warning' },
      { text: '  Cost of delay: ~2hr rework per week unshipped', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  → Wire to production NOW or document why not', type: 'accent' },
    ],
    highlight: { label: 'Ship Rule', value: 'If it validates, it ships. Period.', color: 'red' },
  },

  // ── 7. Error Rate Spike Detection (score: 0.890) ──────
  // Automated monitoring catches issues before users
  {
    id: 'anomaly',
    title: 'Anomaly Detection',
    subtitle: 'Automated monitoring catches issues before users do',
    category: 'Anomaly Detection',
    command: 'cortex anomalies',
    outputLines: [
      { text: '⚠ ANOMALY: Error rate spike', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  Service: /api/v2/forecast', type: 'info' },
      { text: '  Error rate: 28.75% (baseline: 2.1%)', type: 'error' },
      { text: '  Type: metric_spike | Severity: MEDIUM', type: 'warning' },
      { text: '  Detected: 4 minutes ago', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  ✓ 6 other services healthy', type: 'success' },
      { text: '  ✓ Test suite: 5,000+ passing', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  → Check GRIB data pipeline freshness', type: 'accent' },
    ],
    highlight: { label: 'Detection', value: '7 anomaly types, 5-minute intervals', color: 'yellow' },
  },

  // ── 8. Confidence Calibration (score: 0.870) ──────────
  // The system earns trust through measured accuracy
  {
    id: 'calibration',
    title: 'Confidence Calibration',
    subtitle: 'When Cortex says 80% confident — is it right 80% of the time?',
    category: 'Outcome Learning',
    command: 'cortex calibration',
    outputLines: [
      { text: '═══ Confidence Calibration (90 days) ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  Predicted 90%+   →  Actual 87.3%  ~overconfident', type: 'warning' },
      { text: '  Predicted 70-80% →  Actual 74.1%  ✓ calibrated', type: 'success' },
      { text: '  Predicted 50-60% →  Actual 52.8%  ✓ calibrated', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  Recommendation accuracy: 73% (30-day window)', type: 'info' },
      { text: '  Best: Bug fixes (89%) | Worst: Architecture (51%)', type: 'dimmed' },
      { text: '  Trend: +8% improvement since January', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  → Trust at 70%+ confidence. Hedge below.', type: 'accent' },
    ],
    highlight: { label: 'Learning', value: 'Every outcome improves the next recommendation', color: 'green' },
  },
];

export const CYCLE_DURATION_MS = 9000;
